import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, RotateCcw } from "lucide-react";

const STATUS_CHIPS = {
  "Pending Receipt": "bg-amber-50 text-amber-700 border-amber-200",
  "Received":        "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Used":            "bg-sky-50 text-sky-700 border-sky-200",
  "Partially Used":  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Return Pending":  "bg-orange-50 text-orange-700 border-orange-200",
  "Settled":         "bg-slate-100 text-slate-700 border-slate-300",
};

export default function SentSpareDetails() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ technician: "", complaint: "", pod: "", status: "" });
  const [returnTarget, setReturnTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/spares/sent-spare-details");
      setRows(data.rows || []);
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    const f = filters;
    if (f.technician && !(r.technician_name || "").toLowerCase().includes(f.technician.toLowerCase())) return false;
    if (f.complaint && !(r.complaint_number || "").toLowerCase().includes(f.complaint.toLowerCase())) return false;
    if (f.pod && !(r.pod_number || "").toLowerCase().includes(f.pod.toLowerCase())) return false;
    if (f.status && r.status !== f.status) return false;
    return true;
  }), [rows, filters]);

  return (
    <div className="space-y-4" data-testid="sent-spare-details">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Sent Spare Details</h1>
        <p className="text-sm text-slate-500 mt-1">
          Every dispatched spare · per-technician lifecycle · use "Return From Technician" to settle returns.
        </p>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <FilterInput label="Technician" value={filters.technician}
                         onChange={(v) => setFilters({ ...filters, technician: v })} tid="ssd-filter-tech" />
            <FilterInput label="Complaint #" value={filters.complaint}
                         onChange={(v) => setFilters({ ...filters, complaint: v })} tid="ssd-filter-complaint" />
            <FilterInput label="POD #" value={filters.pod}
                         onChange={(v) => setFilters({ ...filters, pod: v })} tid="ssd-filter-pod" />
            <div>
              <Label className="text-xs">Status</Label>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      data-testid="ssd-filter-status">
                <option value="">All</option>
                {Object.keys(STATUS_CHIPS).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" className="w-full"
                      onClick={() => setFilters({ technician: "", complaint: "", pod: "", status: "" })}
                      data-testid="ssd-filter-reset">Reset</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="px-3 py-2 border-b text-xs text-slate-600">
            {filtered.length} of {rows.length} rows {loading && "· loading…"}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <Th>Complaint</Th><Th>Technician</Th><Th>POD</Th><Th>Part</Th>
                  <Th align="right">Sent</Th><Th align="right">Recd</Th><Th align="right">Used</Th>
                  <Th align="right">Ret P</Th><Th align="right">Ret C</Th><Th align="right">Rem</Th>
                  <Th>Status</Th><Th>Sent</Th><Th>Recd</Th><Th align="right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={14} className="py-10 text-center text-slate-400">No dispatches.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.dispatch_id} data-testid={`ssd-row-${r.dispatch_id}`}>
                    <td className="px-2 py-1.5 font-mono">{r.complaint_number || "—"}</td>
                    <td className="px-2 py-1.5">{r.technician_name || "—"}</td>
                    <td className="px-2 py-1.5 font-mono">{r.pod_number || "—"}</td>
                    <td className="px-2 py-1.5">
                      <div className="font-medium text-slate-800">{r.part_name || "—"}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{r.part_code || "—"}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.qty_sent}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.qty_received}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.qty_used}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-orange-700">{r.qty_returned_pending}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-700">{r.qty_returned_confirmed}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{r.qty_remaining}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${STATUS_CHIPS[r.status] || "bg-slate-50 border-slate-200 text-slate-700"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-slate-500">{r.sent_date || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-500">{r.received_date || "—"}</td>
                    <td className="px-2 py-1.5 text-right">
                      {r.qty_remaining > 0 && r.status !== "Pending Receipt" && (
                        <Button size="sm" variant="outline"
                                className="border-amber-300 text-amber-700 hover:bg-amber-50 h-7 text-xs"
                                onClick={() => setReturnTarget(r)}
                                data-testid={`ssd-return-btn-${r.dispatch_id}`}>
                          <RotateCcw size={11} className="mr-1" /> Return
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {returnTarget && (
        <ReturnFromTechDialog row={returnTarget}
                              onClose={() => setReturnTarget(null)}
                              onDone={() => { setReturnTarget(null); load(); }} />
      )}
    </div>
  );
}

function ReturnFromTechDialog({ row, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    qty: Math.max(1, row.qty_remaining), condition: "Damaged",
    destination_bucket: "damaged", warehouse: row.from_warehouse || "KTM",
    rack_number: "", box_number: "", remarks: "", photo_url: "",
  });
  const readFileAsDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onerror = rej;
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Photo must be image"); return; }
    if (f.size > 4_500_000) { toast.error("Photo too large"); return; }
    const dataUrl = await readFileAsDataUrl(f);
    setForm((s) => ({ ...s, photo_url: dataUrl }));
  };
  const submit = async () => {
    if (Number(form.qty) < 1) { toast.error("Qty must be >= 1"); return; }
    if (form.condition === "Damaged" && !form.photo_url) {
      toast.error("Photo required for damaged returns"); return;
    }
    setBusy(true);
    try {
      await api.post("/spares/returns", {
        dispatch_id: row.dispatch_id,
        qty: Number(form.qty),
        condition: form.condition,
        destination_bucket: form.destination_bucket,
        warehouse: form.warehouse,
        rack_number: form.rack_number || undefined,
        box_number: form.box_number || undefined,
        remarks: form.remarks || undefined,
        photo_url: form.photo_url || undefined,
      });
      toast.success("Return recorded · damaged stock updated");
      onDone();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="return-from-tech-dialog">
        <DialogHeader>
          <DialogTitle>Return From Technician · {row.complaint_number}</DialogTitle>
          <DialogDescription>
            POD {row.pod_number} · {row.part_name} ({row.part_code || "—"}) · {row.technician_name}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <Label className="text-xs">Quantity</Label>
            <Input type="number" min={1} max={row.qty_remaining} value={form.qty}
                   onChange={(e) => setForm({ ...form, qty: e.target.value })}
                   data-testid="rft-qty" />
            <div className="text-[10px] text-slate-500 mt-1">Available with technician: {row.qty_remaining}</div>
          </div>
          <div>
            <Label className="text-xs">Condition</Label>
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                    value={form.condition}
                    onChange={(e) => setForm({ ...form, condition: e.target.value })}
                    data-testid="rft-condition">
              {["Damaged", "Defective", "Used", "Replaced", "Other"].map((x) =>
                <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Stock Destination</Label>
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                    value={form.destination_bucket}
                    onChange={(e) => setForm({ ...form, destination_bucket: e.target.value })}
                    data-testid="rft-destination">
              <option value="damaged">Damaged Stock</option>
              <option value="refurbished">Refurbished Stock</option>
              <option value="scrap">Scrap / Rejected</option>
              <option value="fresh">Fresh (rare)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Warehouse</Label>
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                    value={form.warehouse}
                    onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
                    data-testid="rft-warehouse">
              <option value="KTM">KTM</option><option value="BRT">BRT</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Rack Number</Label>
            <Input value={form.rack_number} onChange={(e) => setForm({ ...form, rack_number: e.target.value })}
                   data-testid="rft-rack" />
          </div>
          <div>
            <Label className="text-xs">Box Number</Label>
            <Input value={form.box_number} onChange={(e) => setForm({ ...form, box_number: e.target.value })}
                   data-testid="rft-box" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Photo {form.condition === "Damaged" && <span className="text-rose-600">*</span>}</Label>
            <Input type="file" accept="image/*" onChange={onPhoto} data-testid="rft-photo" />
            {form.photo_url && <img src={form.photo_url} alt="" className="h-16 w-16 object-cover rounded border mt-1" />}
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Remarks</Label>
            <Textarea rows={2} value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      data-testid="rft-remarks" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="rft-submit">{busy ? "Saving…" : "Confirm Return"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilterInput({ label, value, onChange, tid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Search size={12} className="absolute left-2 top-2.5 text-slate-400" />
        <Input className="pl-6 h-9 text-sm" value={value}
               onChange={(e) => onChange(e.target.value)} data-testid={tid} />
      </div>
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-[10px] uppercase tracking-wide text-slate-600 font-semibold px-2 py-1.5`}>{children}</th>;
}
